import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, AlertCircle, Clock, Wallet, Package } from 'lucide-react';
import api from '../../services/api';

// Export function to clear KYC cache when status changes
export const clearKYCCache = () => {
  localStorage.removeItem('kyc_status_cache');
};

const KYCBanner = () => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      // Check cache first (5 minute TTL)
      const cached = localStorage.getItem('kyc_status_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < 5 * 60 * 1000) { // 5 minutes
          setKycStatus(data);
          setLoading(false);
          return;
        }
      }

      // Fetch from API
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
      
      // Cache the result
      localStorage.setItem('kyc_status_cache', JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      // Don't block UI on error
    } finally {
      setLoading(false);
    }
  };

  if (loading || dismissed) return null;

  // Don't show banner if KYC is verified
  if (kycStatus?.status === 'verified') return null;

  const getStatusConfig = () => {
    switch (kycStatus?.status) {
      case 'not_submitted':
        return {
          icon: Shield,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600',
          title: 'Complete Your KYC Verification',
          message: 'Enable wallet usage and COD payments by completing your KYC verification.',
          actionText: 'Complete KYC Now',
          actionLink: '/profile/kyc',
          actionClass: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
      case 'pending':
      case 'under_review':
        return {
          icon: Clock,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600',
          title: 'KYC Verification Pending',
          message: 'Your KYC documents are under review. We\'ll notify you once verified.',
          actionText: 'View Status',
          actionLink: '/profile/kyc',
          actionClass: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      case 'rejected':
        return {
          icon: XCircle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          iconColor: 'text-red-600',
          title: 'KYC Verification Rejected',
          message: kycStatus.rejection_reason || 'Your KYC was rejected. Please resubmit with correct documents.',
          actionText: 'Resubmit KYC',
          actionLink: '/profile/kyc',
          actionClass: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'reupload_requested':
        return {
          icon: AlertCircle,
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
          iconColor: 'text-orange-600',
          title: 'Document Re-upload Required',
          message: 'Please re-upload the requested documents to complete your KYC.',
          actionText: 'Upload Documents',
          actionLink: '/profile/kyc',
          actionClass: 'bg-orange-600 hover:bg-orange-700 text-white'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const StatusIcon = config.icon;

  return (
    <div className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4 mb-6 shadow-sm`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className={`${config.iconColor} mt-0.5`}>
            <StatusIcon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-base ${config.textColor} mb-1`}>
              {config.title}
            </h3>
            <p className={`${config.textColor} text-sm mb-3 opacity-90`}>
              {config.message}
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to={config.actionLink}
                className={`inline-flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors ${config.actionClass}`}
              >
                {config.actionText}
              </Link>
              
              {kycStatus && (
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    <span className={kycStatus.can_book_shipment ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {kycStatus.can_book_shipment ? 'Booking Enabled' : 'Booking Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Wallet className="w-4 h-4" />
                    <span className={kycStatus.can_use_wallet ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {kycStatus.can_use_wallet ? 'Wallet Enabled' : 'Wallet Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    <span className={kycStatus.can_use_cod ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {kycStatus.can_use_cod ? 'COD Enabled' : 'COD Disabled'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-600 ml-4 transition-colors"
          aria-label="Dismiss"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default KYCBanner;
